/**
 * productionRegister · P17-UAT-12 REV-A
 *
 * Pure, side-effect-free helpers shared by the ProductionEntryRegister
 * component and its unit tests. No React, no network, no mutation.
 */

const includesCI = (hay, needle) =>
  !needle || String(hay ?? "").toLowerCase().includes(String(needle).toLowerCase());

/**
 * Filter production entries by an inclusive date range + optional text
 * filters. `opNameOf(id)` resolves an operator display name for matching.
 */
export function filterEntries(rows, filters, opNameOf = (v) => v) {
  const f = filters || {};
  const inRange = (d) =>
    (!f.fromDate || d >= f.fromDate) && (!f.toDate || d <= f.toDate);
  return (rows || []).filter((r) => {
    if (!inRange(r.entry_date || "")) return false;
    if (!includesCI(r.entry_no, f.entryNo)) return false;
    if (!includesCI(r.source_order_no, f.order)) return false;
    if (!includesCI(r.parent_wo_no, f.parentWo)) return false;
    if (!includesCI(r.cwo_no, f.cwo)) return false;
    if (!includesCI(r.rm_code, f.rm)) return false;
    if (!includesCI(r.created_by, f.createdBy)) return false;
    if (f.operator) {
      const on = `${r.operator_id || ""} ${opNameOf(r.operator_id) || ""}`;
      if (!includesCI(on, f.operator)) return false;
    }
    if (f.search) {
      const blob = [
        r.entry_no, r.source_order_no, r.parent_wo_no, r.cwo_no, r.rm_code,
        (r.input_lots || []).join(" "), r.output_sf_code, r.output_batch,
      ].join(" ");
      if (!includesCI(blob, f.search)) return false;
    }
    return true;
  });
}

/**
 * UOM-safe period summary. Quantities are grouped by `uom` and NEVER
 * aggregated across unlike units.
 */
export function summarize(rows) {
  const byUom = {};
  (rows || []).forEach((r) => {
    const u = r.uom || "—";
    const s = byUom[u] || { produced: 0, wastage: 0, consumption: 0 };
    s.produced += Number(r.produced_qty || 0);
    s.wastage += Number(r.wastage_qty || 0);
    s.consumption += Number(r.total_rm_consumption || 0);
    byUom[u] = s;
  });
  // Round to 4 dp to avoid float noise in display/assertions.
  Object.values(byUom).forEach((s) => {
    s.produced = Number(s.produced.toFixed(4));
    s.wastage = Number(s.wastage.toFixed(4));
    s.consumption = Number(s.consumption.toFixed(4));
  });
  return { count: (rows || []).length, byUom };
}
